import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../utils/scrollLock";
import { FaTimes } from "react-icons/fa";
import changelogData from "../data/changelog.json";

type ChangelogModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const ChangelogModal: React.FC<ChangelogModalProps> = ({
  isOpen,
  onClose,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    lockBodyScroll(); // Lock body scroll when modal opens

    const prevActive = document.activeElement as HTMLElement | null;

    // Focus the close button when the modal opens
    const id = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
      setIsVisible(true);
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      unlockBodyScroll(); // Unlock body scroll when modal closes
      document.removeEventListener("keydown", onKeyDown);
      if (prevActive) {
        prevActive.focus();
      }
      window.cancelAnimationFrame(id);
      setIsVisible(false);
      setIsExiting(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const closeWithAnimation = (cb: () => void) => {
    setIsExiting(true);
    setTimeout(() => {
      cb();
    }, 300);
  };

  const handleClose = () => closeWithAnimation(onClose);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="changelog-modal-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
          opacity: isExiting ? 0 : isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {/* Modal Card */}
      <div
        className="changelog-modal-card"
        style={{
          position: "relative",
          width: "min(700px, 92vw)",
          height: "min(80vh, 600px)",
          background: "var(--modal-bg)",
          color: "rgba(255, 255, 255, 0.9)",
          borderRadius: "14px",
          padding: "24px",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          transform: isExiting
            ? "translateY(40px)"
            : isVisible
              ? "translateY(0)"
              : "translateY(40px)",
          opacity: isExiting ? 0 : isVisible ? 1 : 0,
          transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: "20px",
            paddingBottom: "12px",
            // borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div />
            <button
              ref={closeButtonRef}
              type="button"
              onClick={handleClose}
              style={{
                padding: "8px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(156, 163, 175, 0.2)",
                color: "rgba(255, 255, 255, 0.7)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  "rgba(156, 163, 175, 0.3)";
                e.currentTarget.style.color = "rgba(255, 255, 255, 0.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  "rgba(156, 163, 175, 0.2)";
                e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)";
              }}
              aria-label="Close changelog"
            >
              {FaTimes({})}
            </button>
          </div>
          <h2
            id="changelog-modal-title"
            style={{
              margin: "16px 0 0 0",
              fontSize: "24px",
              fontWeight: 600,
              color: "rgba(255, 255, 255, 0.95)",
              textAlign: "center",
            }}
          >
            Welcome to Polytrome!
          </h2>
        </div>

        {/* Scrollable Content */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: "8px",
            marginRight: "-8px",
          }}
        >
          {/* Friendly intro for new and returning users */}
          <div
            style={{
              marginBottom: "24px",
              paddingBottom: "16px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "15px",
                lineHeight: 1.6,
                color: "rgba(255, 255, 255, 0.9)",
              }}
            >
              Welcome to Polytrome, a free metronome with tons of advanced
              features for musicians of all levels. Make sure to check out the
              About page for more info about all the features Polytrome has to
              offer.
            </p>
            <p
              style={{
                marginTop: "10px",
                marginBottom: 0,
                fontSize: "13px",
                lineHeight: 1.6,
                color: "rgba(255, 255, 255, 0.7)",
              }}
            >
              You can always revisit this changelog later from the corner/side
              menu if you want to see what&apos;s been added or improved.
            </p>
          </div>

          <h3
            style={{
              margin: "0 0 16px 0",
              fontSize: "18px",
              fontWeight: 600,
              color: "rgba(255, 255, 255, 0.92)",
            }}
          >
            What's new
          </h3>

          {changelogData.map((entry, index) => (
            <div
              key={entry.version}
              style={{
                marginBottom: index < changelogData.length - 1 ? "32px" : "0",
              }}
            >
              {/* Version Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "rgba(255, 255, 255, 0.95)",
                  }}
                >
                  v{entry.version}
                </h3>
                <span
                  style={{
                    fontSize: "14px",
                    color: "rgba(255, 255, 255, 0.6)",
                    padding: "4px 8px",
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "6px",
                  }}
                >
                  {entry.date}
                </span>
                {index === 0 && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "rgba(16, 185, 129, 0.8)",
                      padding: "4px 8px",
                      background: "rgba(16, 185, 129, 0.1)",
                      borderRadius: "6px",
                      fontWeight: 500,
                    }}
                  >
                    Latest
                  </span>
                )}
              </div>

              {/* Changes List */}
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "20px",
                  listStyle: "none",
                }}
              >
                {entry.changes.map((change, changeIndex) => (
                  <li
                    key={changeIndex}
                    style={{
                      marginBottom: "8px",
                      position: "relative",
                      paddingLeft: "16px",
                      lineHeight: 1.5,
                      color: "rgba(255, 255, 255, 0.85)",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "8px",
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background:
                          index === 0
                            ? "rgba(16, 185, 129, 0.8)"
                            : "rgba(255, 255, 255, 0.4)",
                      }}
                    />
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
};
